import React, { Fragment, useEffect } from "react"
import PropTypes from "prop-types"
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useExpanded,
  usePagination,
} from "react-table"
import { Table, Row, Col, Button } from "reactstrap"
import JobListGlobalFilter from "../../components/Common/GlobalSearchFilter"
import { Link } from "react-router-dom"
import { ORDER_STATUS } from "constants/AppConstants"
import TableSkeletonLoader from "./TabelSkeletionLoader"
import { createSelector } from "reselect"
import { useSelector } from "react-redux"
import { setSystemError } from "store/actions"
import { useDispatch } from "react-redux"

const normalizeSearchQuery = (input = "") => {
  const cleanedInput = String(input).toLowerCase();
  const normalizedQuery = cleanedInput
    .replace(/[-_:/\\|]+/g, " ")
    .replace(/[!@#$%^&*()_+\-={}[\]?/.,<>`~:;"'|\\]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  const tokens = normalizedQuery
    .split(" ")
    .filter(Boolean);

  return {
    normalizedQuery: tokens.join(" "),
    tokens,
  };
};

const getValueByPath = (obj, path) => {
  if (!obj) return "";
  return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : ""), obj);
};

const buildSearchIndex = row => {
  const rowValuesText = Object.values(row?.values || {})
    .filter(value => value !== null && value !== undefined)
    .map(value => String(value))
    .join(" ");

  const rowOriginal = row?.original || {};
  const prioritizedPaths = [
    "customer.name",
    "customer.fullName",
    "customer.firstName",
    "customer.lastName",
    "zone.name",
    "toy.name",
    "toy.toyName",
    "toy.code",
    "toy.toyCode",
    "toyCode",
    "code",
    "name",
  ];

  const prioritizedText = prioritizedPaths
    .map(path => getValueByPath(rowOriginal, path))
    .filter(Boolean)
    .join(" ");

  const fullRowText = JSON.stringify(rowOriginal || {});

  return normalizeSearchQuery(`${rowValuesText} ${prioritizedText} ${fullRowText}`).normalizedQuery;
};

const globalSearchFilter = (rows, _columnIds, filterValue) => {
  const { tokens } = normalizeSearchQuery(filterValue || "");
  if (tokens.length === 0) return rows;

  return rows.filter(row => {
    const searchIndex = buildSearchIndex(row);
    return tokens.every(token => searchIndex.includes(token));
  });
};

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  isJobListGlobalFilter,
  gotoPage,
  setPage,
  setInputVal
}) {
  const count = preGlobalFilteredRows.length
  const [value, setValue] = React.useState(globalFilter)
  const onChange = useAsyncDebounce(payload => {
    const normalizedValue = payload?.normalizedQuery || "";
    setGlobalFilter(normalizedValue || undefined);
    setInputVal(normalizedValue);
  }, 200)

  return (
    <React.Fragment>
      <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
        <div className="d-flex flex-column gap-1">
          <label className="form-label mb-1">Search:</label>
          <input
            type="search"
            className="form-control"
            id="search-bar-0"
            value={value || ""}
            placeholder={`${count} records...`}
            onChange={e => {
              gotoPage(0);
              setPage(1);
              setValue(e.target.value);
              onChange(normalizeSearchQuery(e.target.value))
            }}
          />
        </div>
      </Col>
      {isJobListGlobalFilter && <JobListGlobalFilter setGlobalFilter={setGlobalFilter} />}
    </React.Fragment>
  )
}
function CustomGlobalFilter({
  setQuery,
  gotoPage,
  limit,
  setPage,
  totals,
  query,
  colProps,
  rawSearch = false
}) {
  const [value, setValue] = React.useState(query);
  useEffect(() => {
    if (!rawSearch) setValue(query);
  }, [query, rawSearch]);
  const onChange = useAsyncDebounce(payload => {
    const normalizedValue = payload?.normalizedQuery || "";
    setQuery(normalizedValue || "")
  }, 200)

  return (
    <React.Fragment>
      <Col xs={12} sm={6} md={4} lg={3} xl={2} {...colProps} className="mb-2">
        <div className="d-flex flex-column gap-1">
          <label className="form-label mb-1">Search:</label>
          <input
            type="search"
            className="form-control"
            id="search-bar-0"
            value={value || ""}
            placeholder={`${totals || 0} records...`}
            onChange={e => {
              gotoPage(0);
              setPage(1);
              setValue(e.target.value);
              // rawSearch (opt-in): send the text as typed/pasted — the API does its own matching
              onChange(rawSearch ? { normalizedQuery: e.target.value.trim() } : normalizeSearchQuery(e.target.value))
            }}
          />
        </div>
      </Col>
    </React.Fragment>
  )
}



const TableContainer = ({
  title,
  columns,
  data,
  isGlobalFilter,
  isCustomGlobalFilter,
  searchColProps,
  rawSearch = false,
  setQuery,
  query,
  isJobListGlobalFilter,
  isAddOptions,
  alignAddWithInputs = false,
  isAddUserList,
  handleOrderClicks,
  handleUserClick,
  handleCustomerClick,
  isAddCustList,
  customPageSize,
  customPageSizeOptions,
  iscustomPageSizeOptions,
  isPagination,
  isShowingPageLength,
  paginationDiv,
  pagination,
  tableClass,
  theadClass,
  isZoneOptions,
  zones,
  zone,
  setZone,
  isWeekOptions,
  week,
  setWeek,
  csvExport,
  exportToCSVButtonClicked,
  pickUpType,
  setPickUpType,
  pickUpPoints,
  pickUpPoint,
  isPickUpPointOptions,
  setpickUpPoint,
  isCityFilter,
  cities,
  city,
  setCity,
  isCouponCategoryFilter,
  couponCategory,
  setCouponCategory,
  isPackageFilter = false,
  packages = [],
  packageId = "All",
  setPackageId = () => {},
  orderChecked = [],
  setOrderChecked,
  allChecked = false,
  setAllChecked,
  isCheckable = false,
  setOrderStatus,
  orderStatus = "pending",
  toyPage = false,
  setOpenChangeStatusModal,
  isStartDate,
  startDate,
  setStartDate,
  isEndDate,
  endDate,
  DateFilterTitle,
  setEndDate,
  isCustomPagination,
  setPage,
  pageNumber,
  totals = 0,
  toyStatusFilter,
  setToyStatusFilter,
  categories,
  toyCategoryFilter,
  setToyCategoryFilter,
  toyShowFilter,
  setToyShowFilter,
  toyFeaturedFilter,
  setToyFeaturedFilter,
  setLimit,
  isDate,
  date,
  setDate,
  addOrder,
  handleAddOrderClick,
  nextPageLink,
  renderRowSubComponent
}) => {
  const [inputVal, setInputVal] = React.useState("");
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    visibleColumns,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      // defaultColumn: { Filter: DefaultColumnFilter },
      manualPagination: isCustomPagination,
      pageCount: isCustomPagination ? Math.ceil(totals / customPageSize) : -1,
      initialState: {
        pageIndex: isCustomPagination ? 0 : (pageNumber || 0),
        pageSize: customPageSize,
        globalFilter: inputVal || "",
      },
      globalFilter: globalSearchFilter,
    },
    useGlobalFilter,
    // useFilters,
    useSortBy,
    useExpanded,
    usePagination
  )

  useEffect(() => {
    if (setPage && typeof (setPage) == "function") {
      setPage(1);
    }
  }, [inputVal, startDate, endDate, city, pickUpType, pickUpPoint, toyStatusFilter, toyCategoryFilter, toyShowFilter, toyFeaturedFilter, orderStatus, zone, week, pageSize])

  const generateSortingIndicator = column => {
    return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""
  }

  const onChangeInSelect = event => {
    setPageSize(Number(event.target.value))
    if (setLimit) {
      setLimit(Number(event.target.value))
    }
  }

  const paginationArray = [];
  for (let i = 0; i < Math.ceil(totals / pageSize); i++) {
    paginationArray.push(i + 1);
  }

  useEffect(() => {
    if (setPage && typeof (setPage) == "function" && isCustomPagination !== true) {
      setPage(pageIndex);
    }
  }, [pageIndex])

  const systemState = (state) => state.systemState;
  const systemStateProperties = createSelector(
    systemState,
    systemState => ({
      loading: systemState.loading,
      error: systemState.error
    })
  );
  const { loading, error } = useSelector(systemStateProperties);
  const dispatch = useDispatch();
  return (
    <Fragment>
      <Row className="mb-2">
        {/* {iscustomPageSizeOptions &&
          <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
            <div className="d-flex flex-column gap-1">
              <label className="form-label mb-1">Show:</label>
              <select
                className="form-select"
                value={pageSize}
                onChange={onChangeInSelect}
              >
                {[10, 20, 30, 40, 50].map(pageSize => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </select>
            </div>
          </Col>
        } */}

        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            isJobListGlobalFilter={isJobListGlobalFilter}
            gotoPage={gotoPage}
            setPage={setPage ? setPage : () => { }}
            setInputVal={setInputVal}
          />
        )}

        {isCustomGlobalFilter && (
          <CustomGlobalFilter
            setQuery={setQuery}
            gotoPage={gotoPage}
            limit={pageSize}
            setPage={setPage ? setPage : () => { }}
            totals={totals}
            query={query}
            colProps={searchColProps}
            rawSearch={rawSearch}
          />
        )}

        {isZoneOptions && <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
          <div className="d-flex flex-column gap-1">
            <label htmlFor="zone" className="form-label mb-1">Zone:</label>
            <select
              id="zone"
              className="form-select"
              value={zone}
              onChange={(event) => setZone(event.target.value)}
            >
              <option value="all">All</option>
              {zones?.map(zone => (
                <option key={zone?._id} value={zone?._id}>
                  {zone?.name}
                </option>
              ))}
            </select>
          </div>
        </Col>}

        {/*isStartDate to filter upcoming orders between start and end date */}
        {isStartDate && <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
          <div className="d-flex flex-column gap-1">
            <label htmlFor="startDate" className="form-label mb-1">From:</label>
            <input
              type="date"
              id="startDate"
              className="form-control"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
            {
              DateFilterTitle && <small className="text-primary">*Filtered by {DateFilterTitle}</small>
            }
          </div>
        </Col>}

        {isEndDate && <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
          <div className="d-flex flex-column gap-1">
            <label htmlFor="endDate" className="form-label mb-1">To:</label>
            <input
              type="date"
              id="endDate"
              className="form-control"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>
        </Col>}

        {isDate && <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
          <div className="d-flex flex-column gap-1">
            <label htmlFor="date" className="form-label mb-1">Date:</label>
            <input
              type="date"
              id="date"
              className="form-control"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
            {
              DateFilterTitle && <small className="text-primary">*Filtered by {DateFilterTitle}</small>
            }
          </div>
        </Col>}

        {isWeekOptions && <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
          <div className="d-flex flex-column gap-1">
            <label htmlFor="week" className="form-label mb-1">Orders:</label>
            <select
              id="week"
              className="form-select"
              value={week}
              onChange={(event) => setWeek(event.target.value)}
            >
              {[{
                name: "All",
                value: 0
              }, {
                name: "This Week",
                value: 1
              }, {
                name: "Next Week",
                value: 2
              },
              {
                name: "Next to Next Week",
                value: 3
              }
              ].map((week, i) => (
                <option key={i} value={Number(week.value)}>
                  {week.name}
                </option>
              ))}
            </select>
          </div>
        </Col>}

        {
          isPickUpPointOptions && (
            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
              <div className="d-flex flex-column gap-1">
                <label htmlFor="pickUpType" className="form-label mb-1">Pick-up Type:</label>
                <select
                  id="pickUpType"
                  className="form-select"
                  value={pickUpType}
                  onChange={(event) => {
                    setpickUpPoint("all");
                    setPickUpType(event.target.value)
                  }
                  }
                >
                  <option value="all">All</option>
                  <option value="true">Self</option>
                  <option value="false">Company</option>
                </select>
              </div>
            </Col>
          )
        }

        {
          isCityFilter && (
            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
              <div className="d-flex flex-column gap-1">
                <label htmlFor="city" className="form-label mb-1">City:</label>
                <select
                  id="city"
                  className="form-select"
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                >
                  {
                    cities?.length > 0 ? (
                      <>
                        <option value="All">All</option>
                        {
                          cities?.map((city, i) => (
                            <option key={i} value={city?._id}>
                              {city?.name}
                            </option>
                          ))
                        }
                      </>
                    ) : (
                      <option value="All">No Cities Available</option>
                    )
                  }
                </select>
              </div>
            </Col>
          )
        }
        {
          isCouponCategoryFilter && (
            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
              <div className="d-flex flex-column gap-1">
                <label htmlFor="couponCategory" className="form-label mb-1">Category:</label>
                <select
                  id="couponCategory"
                  className="form-select"
                  value={couponCategory}
                  onChange={(event) => setCouponCategory(event.target.value)}
                >
                  <option value="All">All</option>
                  <option value="order">Order</option>
                  <option value="subscription">Subscription</option>
                </select>
              </div>
            </Col>
          )
        }
        {
          isPackageFilter && (
            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
              <div className="d-flex flex-column gap-1">
                <label htmlFor="package" className="form-label mb-1">Package:</label>
                <select
                  id="package"
                  className="form-select"
                  value={packageId}
                  onChange={(event) => setPackageId(event.target.value)}
                >
                  <option value="All">All</option>
                  {packages?.map(p => (
                    <option key={p?._id} value={p?._id}>
                      {p?.name}
                    </option>
                  ))}
                </select>
              </div>
            </Col>
          )
        }

        {
          isPickUpPointOptions && pickUpType === "true" && (
            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
              <div className="d-flex flex-column gap-1">
                <label htmlFor="pickUpPoint" className="form-label mb-1">Pick-up Point:</label>
                <select
                  id="pickUpPoint"
                  className="form-select"
                  value={pickUpPoint}
                  onChange={(event) => setpickUpPoint(event.target.value)}
                >
                  <option value="all">All</option>
                  {
                    pickUpPoints?.map((point, i) => (
                      <option key={i} value={point?._id}>
                        {point?.storeName}
                      </option>
                    ))
                  }
                </select>
              </div>
            </Col>
          )
        }

        {
          toyStatusFilter && (
            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
              <div className="d-flex flex-column gap-1">
                <label htmlFor="toyStatusFilter" className="form-label mb-1">Status:</label>
                <select
                  id="toyStatusFilter"
                  className="form-select"
                  value={toyStatusFilter}
                  onChange={(event) => {
                    setToyStatusFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="all">All</option>
                  <option value={ORDER_STATUS.AVAILABLE}>{ORDER_STATUS.AVAILABLE}</option>
                  <option value={ORDER_STATUS.RENTED}>{ORDER_STATUS.RENTED}</option>
                  <option value={ORDER_STATUS.ONHOLD}>{ORDER_STATUS.ONHOLD}</option>
                  <option value={ORDER_STATUS.CLEANING}>{ORDER_STATUS.CLEANING}</option>
                  <option value={ORDER_STATUS.BROKEN}>{ORDER_STATUS.BROKEN}</option>
                  <option value={ORDER_STATUS.MAINTENANCE}>{ORDER_STATUS.MAINTENANCE}</option>
                </select>
              </div>
            </Col>
          )
        }

        {
          toyCategoryFilter && (
            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
              <div className="d-flex flex-column gap-1">
                <label htmlFor="toyCategoryFilter" className="form-label mb-1">Category:</label>
                <select
                  id="toyCategoryFilter"
                  className="form-select"
                  value={toyCategoryFilter}
                  onChange={(event) => {
                    setToyCategoryFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="all">All</option>
                  {
                    categories.map((cat, i) =>
                      <option value={cat._id} key={i}>
                        {cat.name}
                      </option>
                    )
                  }
                </select>
              </div>
            </Col>
          )
        }
        {
          toyShowFilter && (
            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
              <div className="d-flex flex-column gap-1">
                <label htmlFor="toyShowFilter" className="form-label mb-1">Show on Website:</label>
                <select
                  id="toyShowFilter"
                  className="form-select"
                  value={toyShowFilter}
                  onChange={(event) => {
                    setToyShowFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="all">All</option>
                  <option value="yes">Active</option>
                  <option value="no">Inactive</option>
                </select>
              </div>
            </Col>
          )
        }
        {
          toyFeaturedFilter && (
            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
              <div className="d-flex flex-column gap-1">
                <label htmlFor="toyFeaturedFilter" className="form-label mb-1">Featured:</label>
                <select
                  id="toyFeaturedFilter"
                  className="form-select"
                  value={toyFeaturedFilter}
                  onChange={(event) => {
                    setToyFeaturedFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="all">All</option>
                  <option value="yes">Active</option>
                  <option value="no">Inactive</option>
                </select>
              </div>
            </Col>
          )
        }

        {
          (orderChecked?.length !== 0 || allChecked) && (
            toyPage ?
              <>
                <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
                  <Button
                    color="primary"
                    className="w-100"
                    onClick={() => setOpenChangeStatusModal(true)}
                  >
                    Change Status
                  </Button>
                </Col>
              </>
              :
              <>
                <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <input type="checkbox" id="markAsDelivered" className="form-check-input m-0"
                      onChange={(e) => {
                        setOrderStatus(e.target.checked ? "delivered" : "pending");
                      }}
                      checked={orderStatus === "delivered"}
                    />
                    <label htmlFor="markAsDelivered" className="form-label m-0">Mark As Delivered</label>
                  </div>
                </Col>
                <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <input type="checkbox" id="markAsReturned" className="form-check-input m-0"
                      onChange={(e) => {
                        setOrderStatus(e.target.checked ? "returned" : "pending");
                      }}
                      checked={orderStatus === "returned"}
                    />
                    <label htmlFor="markAsReturned" className="form-label m-0">Mark As Returned</label>
                  </div>
                </Col>
              </>
          )
        }

        {isAddOptions && (
          <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2 ms-auto">
            {alignAddWithInputs && (
              <label className="form-label mb-1 d-block invisible" aria-hidden="true">.</label>
            )}
            <div className="d-flex justify-content-end">
              <Button
                type="button"
                color="success"
                className={alignAddWithInputs ? "btn-rounded" : "btn-rounded mt-2"}
                onClick={handleOrderClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add New {title}
              </Button>
            </div>
          </Col>
        )}


        {
          addOrder && (
            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2 ms-auto">
              <div className="d-flex justify-content-end">
                <Button
                  type="button"
                  color="success"
                  className="btn"
                  onClick={() => {
                    handleAddOrderClick();
                  }}
                >
                  <i className="mdi mdi-plus me-1" />
                  Add Order
                </Button>
              </div>
            </Col>
          )
        }

        {
          csvExport && (
            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2 ms-auto">
              <div className="d-flex justify-content-end">
                <Button
                  type="button"
                  color="primary"
                  className="btn"
                  onClick={() => {
                    exportToCSVButtonClicked();
                  }}
                >
                  <i className="mdi mdi-file-excel me-1" />
                  Export
                </Button>
              </div>
            </Col>
          )
        }

        {isAddUserList && (
          <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2 ms-auto">
            <div className="d-flex justify-content-end">
              <Button
                type="button"
                color="primary"
                className="btn"
                onClick={handleUserClick}
              >
                <i className="mdi mdi-plus-circle-outline me-1" />
                Create New User
              </Button>
            </div>
          </Col>
        )}
        {isAddCustList && (
          <Col xs={12} sm={6} md={4} lg={3} xl={2} className="mb-2 ms-auto">
            <div className="d-flex justify-content-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded"
                onClick={handleCustomerClick}
              >
                <i className="mdi mdi-plus me-1" />
                New Customers
              </Button>
            </div>
          </Col>
        )}
      </Row>

      <div className="table-responsive">
        {
          error &&
          <div className="alert alert-danger alert-dismissible fade show text-center" role="alert">
            <i className="fas fa-exclamation-circle"></i> {error}
            <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"
              onClick={() => {
                dispatch(setSystemError(false));
              }}
            />
          </div>
        }
        <Table {...getTableProps()} className={tableClass}>
          <thead className={theadClass}>
            {headerGroups.map(headerGroup => (
              <>
                <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                  {
                    isCheckable === true &&
                    <td>
                      <input type="checkbox" className="m-0" key={1}
                        checked={allChecked}
                        onChange={(e) => {
                          setAllChecked(e.target.checked);
                          setOrderChecked(e.target.checked ? data?.map((item) => item._id) : []);
                        }}
                      />
                    </td>
                  }
                  {headerGroup.headers.map(column => (
                    <th key={column.id} className={column.isSort ? "sorting" : ''}>
                      <div className="m-0" {...column.getSortByToggleProps()}>
                        {column.render("Header")}
                      </div>
                      {/* <Filter column={column} /> */}
                    </th>
                  ))}
                </tr>
              </>
            ))}
          </thead>
          {
            loading ?
              <tbody>
                <tr>
                  <td colSpan="100%">
                    <TableSkeletonLoader length={10} />
                  </td>
                </tr>
              </tbody>
              :
              <tbody {...getTableBodyProps()}>
                {
                  data.length === 0 && !loading && (
                    <tr>
                      <td colSpan="100%">
                        <div className="text-center py-4 bg-light border rounded">
                          <i className="fas fa-times-circle fa-2x mb-2 text-danger"></i>
                          <div className="text-danger">No data found</div>
                        </div>
                      </td>
                    </tr>
                  )
                }
                {page.map(row => {
                  prepareRow(row)
                  return (
                    <Fragment key={row.getRowProps().key}>
                      <tr {...row.getRowProps()} onClick={() => renderRowSubComponent && row.toggleRowExpanded()}>
                        {
                          (isCheckable === true) &&
                          <td>
                            <input type="checkbox" className="m-0"
                              checked={allChecked || orderChecked?.includes(row.original._id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setOrderChecked([...orderChecked, row.original._id]);
                                  if (allChecked === true) {
                                    setAllChecked(false);
                                  }
                                } else {
                                  setOrderChecked(orderChecked?.filter((item) => item !== row.original._id));
                                  if (allChecked === true) {
                                    setAllChecked(false);
                                  }
                                }
                              }}
                            />
                          </td>
                        }
                        {row.cells.map(cell => {
                          return (
                            <td key={cell.column.id} {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          )
                        })}
                      </tr>
                      {row.isExpanded && renderRowSubComponent ? (
                        <tr>
                          <td colSpan={visibleColumns.length + (isCheckable ? 1 : 0)}>
                            {renderRowSubComponent(row)}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })}
              </tbody>
          }
        </Table>
      </div>

      {
        loading || page.length === 0 ? null : (
          <>
            {
              isPagination && (
                <Row className="justify-content-end align-items-center">
                  {iscustomPageSizeOptions && (
                    <Col className="col-sm-auto">
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted">Rows per page:</span>
                        <select
                          className="form-select form-select-sm w-auto"
                          value={pageSize}
                          onChange={onChangeInSelect}
                        >
                          {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                              {pageSize}
                            </option>
                          ))}
                        </select>
                      </div>
                    </Col>
                  )}
                  <Col className="col-sm-auto">
                    <div className="text-muted">
                      {data.length > 0 ? (pageIndex * pageSize) + 1 : 0}-
                      {Math.min((pageIndex + 1) * pageSize, data.length)} of {data.length}
                    </div>
                  </Col>
                  <Col className="col-sm-auto">
                    <div className="d-flex gap-1">
                      <Button
                        color="light"
                        className="btn-sm"
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}
                      >
                        <i className="mdi mdi-chevron-left"></i>
                      </Button>
                      <Button
                        color="light"
                        className="btn-sm"
                        onClick={() => nextPage()}
                        disabled={!canNextPage}
                      >
                        <i className="mdi mdi-chevron-right"></i>
                      </Button>
                    </div>
                  </Col>
                </Row>
              )
            }

            {
              isCustomPagination && (
                <Row className="justify-content-end align-items-center">
                  <Col className="col-sm-auto">
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted">Rows per page:</span>
                      <select
                        className="form-select form-select-sm w-auto"
                        value={pageSize}
                        onChange={onChangeInSelect}
                      >
                        {[10, 20, 30, 40, 50].map(pageSize => (
                          <option key={pageSize} value={pageSize}>
                            {pageSize}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Col>
                  <Col className="col-sm-auto">
                    <div className="text-muted">
                      {totals > 0 ? ((pageNumber - 1) * pageSize) + 1 : 0}-
                      {Math.min(pageNumber * pageSize, totals)} of {totals}
                    </div>
                  </Col>
                  <Col className="col-sm-auto">
                    <div className="d-flex gap-1">
                      <Button
                        color="light"
                        className="btn-sm"
                        onClick={() => setPage(pageNumber - 1)}
                        disabled={totals === 0 || pageNumber === 1}
                      >
                        <i className="mdi mdi-chevron-left"></i>
                      </Button>
                      <Button
                        color="light"
                        className="btn-sm"
                        onClick={() => setPage(pageNumber + 1)}
                        disabled={totals === 0 || pageNumber === Math.ceil(totals / pageSize)}
                      >
                        <i className="mdi mdi-chevron-right"></i>
                      </Button>
                    </div>
                  </Col>
                </Row>
              )
            }
          </>
        )
      }
    </Fragment>
  )
}

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
}

export default TableContainer
